import React, { useState, useEffect } from "react";
// @ts-ignore
import { useBeforeunload } from "react-beforeunload";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/react-hooks";
import { AppBar, Box, Dialog, LinearProgress, Typography } from "@mui/material";
import { MyHrUsersImportResult } from "my-hr/components";
import { MY_HR_IMPORT_SSHR_USERS, MY_HR_IMPORT_USERS } from "my-hr/graphql";
import { OcMassRoleToolbar, OcTransition } from "oc/components";
import { OC_UPDATE_PROPERTY } from "oc/graphql";
import { useOcClientState, useOcIsMobile, useOcProperty } from "oc/hooks";

type MyHrUsersImportDialogProps = {
  showDialog: boolean;
  onShowDialog: () => void;
  finishImport: () => void;
  importType: "sshr" | "jdolber";
  onCloseDialog: () => void;
  automaticPasswordGeneration: boolean;
  roleFilters: any; // Replace 'any' with a more specific type if available
};

export const MyHrUsersImportDialog = ({
  showDialog,
  onShowDialog,
  finishImport,
  importType,
  onCloseDialog,
  automaticPasswordGeneration,
  roleFilters,
}: MyHrUsersImportDialogProps) => {
  const isMobile = useOcIsMobile();
  const { t } = useTranslation();

  const [hasImportStarted, setHasImportStarted] = useState(false);

  useBeforeunload((event: BeforeUnloadEvent) => {
    if (!hasImportStarted) return;
    event.preventDefault();
    updateProperty({
      variables: {
        resource: "system",
        key: "importLocked",
        value: "false",
      },
    });
  });

  const [updateProperty] = useMutation(OC_UPDATE_PROPERTY, {
    onError({ ...err }) {
      alert("error" + err.toString());
    },
  });

  // Monitors the value of showDialog and when it changes to false, sets importLocked to false
  useEffect(() => {
    if (!showDialog) {
      updateProperty({
        variables: {
          resource: "system",
          key: "importLocked",
          value: "false",
        },
      });
    }
  }, [showDialog, updateProperty]);

  let IMPORT_MUTATION =
    importType === "sshr" ? MY_HR_IMPORT_SSHR_USERS : MY_HR_IMPORT_USERS;

  let DIALOG_TITLE =
    importType === "sshr"
      ? t("MY_HR_IMPORT_USERS_FROM_SSHR")
      : t("MY_HR_IMPORT_USERS_FROM_JDOLBER");

  let INFO_TEXT =
    importType === "sshr"
      ? t("MY_HR_IMPORT_SSHR_USERS_TEXT_INFO")
      : t("MY_HR_IMPORT_USERS_TEXT_INFO");

  const { data: importProgress, refetch } = useOcProperty({
    resource: "system",
    key: "importProgress",
  });

  const progressInPercentString = Number.isNaN(importProgress)
    ? "0%"
    : `${importProgress}%`;

  const [importUsersResult, setImportUsersResult] = useOcClientState(
    "importSSHRUsersResult",
    {
      importErrors: [],
      failedUsersCount: 0,
      totalProcessedRecordCount: 0,
      importedUsersCount: 0,
    },
  );

  const [importLoading, setImportLoading] = useState(false);

  const [importUsers] = useMutation(IMPORT_MUTATION, {
    onCompleted: (data) => {
      setImportLoading(false);
      if (importType === "sshr") {
        setImportUsersResult(data.importSSHRUsers);
      } else {
        setImportUsersResult(data.importUsers);
      }
      updateProperty({
        variables: {
          resource: "system",
          key: "importProgress",
          value: "100",
        },
      });
      refetch();
    },
    onError: (error) => {
      setImportLoading(false);
      // Always unlock import on any error
      updateProperty({
        variables: {
          resource: "system",
          key: "importLocked",
          value: "false",
        },
      });
      if (error.message === "Import process cancelled") {
        onCloseDialog();
      }
    },
  });

  let showImportResult = Object.keys(importUsersResult).length !== 0;

  const handleEnter = () => {
    setHasImportStarted(true);

    updateProperty({
      variables: {
        resource: "system",
        key: "importLocked",
        value: "true",
      },
    });

    updateProperty({
      variables: {
        resource: "system",
        key: "importCancelled",
        value: "false",
      },
    });

    setImportLoading(true);
    importUsers({ variables: { automaticPasswordGeneration, roleFilters } });
  };

  const handleExit = () => {
    updateProperty({
      variables: {
        resource: "system",
        key: "importProgress",
        value: "0",
      },
    });

    updateProperty({
      variables: {
        resource: "system",
        key: "importLocked",
        value: "false",
      },
    });

    setHasImportStarted(false);
  };

  const cancelImport = () => {
    updateProperty({
      variables: {
        resource: "system",
        key: "importCancelled",
        value: "true",
      },
    });
    handleExit();
    onCloseDialog();
  };

  return (
    <Dialog
      id="import-dialog"
      fullScreen
      open={showDialog}
      onClose={onShowDialog}
      TransitionComponent={OcTransition}
      TransitionProps={{
        onEntered: handleEnter,
        onExited: handleExit,
      }}
    >
      <AppBar
        position="relative"
        elevation={0}
        sx={{
          backgroundColor: "#E3E5E5",
        }}
      >
        <OcMassRoleToolbar
          handleCancel={cancelImport}
          handleFinish={finishImport}
          dialogTitle={DIALOG_TITLE}
          disabled={importProgress !== "100"}
        />
      </AppBar>
      <Box padding="16px">
        <Box
          display="flex"
          alignItems="center"
          marginTop="40px"
          marginBottom="56px"
        >
          <Box
            width={isMobile ? "100%" : "72%"}
            paddingLeft={isMobile ? "0px" : "14%"}
          >
            <LinearProgress
              variant="determinate"
              value={Number.parseInt(importProgress)}
              sx={{
                backgroundColor: "#E3E5E5",
                height: "16px",
                borderRadius: "4px",
              }}
            />
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            paddingRight={isMobile ? "0px" : "14%"}
            paddingLeft="8px"
          >
            <Box>
              <Typography variant="body2" color="#757575">
                {progressInPercentString}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box>{INFO_TEXT}</Box>
      </Box>
      {showImportResult && !importLoading && (
        <Box padding="0px 16px">
          <MyHrUsersImportResult
            importUsersResult={importUsersResult}
            isMobile={isMobile}
          />
        </Box>
      )}
    </Dialog>
  );
};

export default MyHrUsersImportDialog;
